"use client";

import { useEffect } from "react";

type Trigger = "enter" | "middle";

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function getObserverOptions(el: HTMLElement): IntersectionObserverInit {
  const trigger = (el.dataset.scrollfxTrigger ?? "enter") as Trigger;
  const threshold = Number(el.dataset.scrollfxThreshold ?? "0") || 0;

  if (trigger === "enter") {
    return { root: null, rootMargin: "0px", threshold };
  }

  // middle trigger: use a band around screen center
  const band = clamp(Number(el.dataset.scrollfxBand ?? "0.2") || 0.2, 0, 1);
  const shrink = ((1 - band) / 2) * 100; // percent removed from top and bottom
  const rootMargin = `-${shrink}% 0px -${shrink}% 0px`;

  return { root: null, rootMargin, threshold };
}

function makeKey(opts: IntersectionObserverInit) {
  return `${opts.rootMargin ?? ""}|${JSON.stringify(opts.threshold ?? 0)}`;
}

export const useGraphicEffects = () => {
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;

    const observed = new WeakSet<HTMLElement>();
    const observers = new Map<string, IntersectionObserver>();

    const ensureObserved = (el: HTMLElement) => {
      if (observed.has(el)) return;
      observed.add(el);

      const opts = getObserverOptions(el);
      const key = makeKey(opts);

      if (!observers.has(key)) {
        const obs = new IntersectionObserver((entries, observer) => {
          for (const entry of entries) {
            const target = entry.target as HTMLElement;
            const once = (target.dataset.scrollfxOnce ?? "true") === "true";

            if (entry.isIntersecting) {
              target.classList.add("is-inview");
              if (once) observer.unobserve(target);
            } else {
              if (!once) target.classList.remove("is-inview");
            }
          }
        }, opts);

        observers.set(key, obs);
      }

      observers.get(key)!.observe(el);
    };

    const scan = (root: ParentNode = document) => {
      const els = root.querySelectorAll<HTMLElement>('[data-scrollfx="true"]');
      els.forEach(ensureObserved);
    };

    // initial scan
    scan();

    // observe elements added later (Next route changes / conditional rendering)
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          // if the node itself matches
          if (node.matches?.('[data-scrollfx="true"]')) ensureObserved(node);

          // or any children match
          scan(node);
        });
      }
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      observers.forEach((obs) => obs.disconnect());
      observers.clear();
    };
  }, []);
};
